import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Users, Clock, AlertCircle, Check, Briefcase } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { toast } from 'sonner';
import api from '../lib/api';
import SuccessModal from '../components/SuccessModal';

const urgencyLevels = [
  { value: "normal", label: "Normal (7-14 jours)", color: "text-green-400" },
  { value: "urgent", label: "Urgent (3-7 jours)", color: "text-amber-400" },
  { value: "very_urgent", label: "Très Urgent (1-3 jours)", color: "text-orange-400" },
  { value: "immediate", label: "Immédiat (24h)", color: "text-red-400" }
];

const highlights = [
  "Accompagnement personnalisé",
  "Traitement prioritaire disponible",
  "Suivi en temps réel de votre dossier",
  "Équipe multilingue",
  "Présence à Kinshasa et Dubaï"
];

export const ConciergeriePage = () => {
  const navigate = useNavigate();
  const [services, setServices] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [formData, setFormData] = useState({
    num_adults: 1,
    num_minors: 0,
    requester_type: 'adult',
    full_name: '',
    company_name: '',
    email: '',
    phone: '',
    service: '',
    urgency: 'normal',
    additional_info: ''
  });

  useEffect(() => {
    fetchServices();
  }, []);

  const fetchServices = async () => {
    try {
      const response = await api.get('/services/conciergerie');
      setServices(response.data);
    } catch (error) {
      console.error('Error fetching services:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.service) {
      toast.error('Veuillez sélectionner un service');
      return;
    }
    setSubmitting(true);
    try {
      await api.post('/services/conciergerie', formData);
      setFormData({
        num_adults: 1, num_minors: 0, requester_type: 'adult', full_name: '',
        company_name: '', email: '', phone: '', service: '', urgency: 'normal', additional_info: ''
      });
      setShowSuccess(true);
    } catch (error) {
      toast.error("Erreur lors de l'envoi");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-[#020617] min-h-screen">
      {/* Header */}
      <section className="pt-6 pb-4 relative">
        <div className="absolute inset-0 gradient-radial opacity-30" />
        <div className="max-w-7xl mx-auto px-6 md:px-12 relative">
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-2 text-[#94A3B8] hover:text-[#D4AF37] transition-colors mb-4"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-4 h-4" />
            Retour à l'accueil
          </button>

          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-lg bg-[#D4AF37]/10 border border-[#D4AF37]/20 flex items-center justify-center">
              <Users className="w-5 h-5 text-[#D4AF37]" />
            </div>
            <div>
              <h1 className="text-xl md:text-3xl font-bold text-white">Conciergerie</h1>
              <p className="text-[#94A3B8] text-xs">Services administratifs et documents officiels</p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Content - 2 Columns */}
      <section className="py-4">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="max-w-xl mx-auto flex flex-col gap-6">
            {/* Left - Info */}
            <div className="space-y-3">
              <motion.div className="card-luxury p-4" initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }}>
                <div className="flex items-center gap-2 mb-3">
                  <Briefcase className="w-4 h-4 text-[#D4AF37]" />
                  <h3 className="text-xs font-semibold text-white">Nos Avantages</h3>
                </div>
                <ul className="space-y-2">
                  {highlights.map((item, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <Check className="w-3 h-3 text-green-400 flex-shrink-0 mt-0.5" />
                      <span className="text-[#94A3B8] text-xs">{item}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>

              <motion.div className="card-luxury p-4" initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.1 }}>
                <div className="flex items-center gap-2 mb-3">
                  <Clock className="w-4 h-4 text-[#D4AF37]" />
                  <h3 className="text-xs font-semibold text-white">Niveaux d'Urgence</h3>
                </div>
                <div className="space-y-2">
                  {urgencyLevels.map((level) => (
                    <div key={level.value} className="flex items-center justify-between p-2 rounded-lg bg-white/5 border border-white/5">
                      <span className={`text-xs font-medium ${level.color}`}>{level.label}</span>
                    </div>
                  ))}
                </div>
              </motion.div>

              <motion.div className="card-luxury p-3 bg-gradient-to-br from-amber-500/10 to-transparent border-amber-500/20" initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.2 }}>
                <h4 className="font-semibold text-amber-400 mb-1 text-xs">Information</h4>
                <p className="text-[#94A3B8] text-xs">Les délais varient selon le type de service et le niveau d'urgence choisi.</p>
              </motion.div>
            </div>

            {/* Right - Form */}
            <motion.div
              className="card-luxury p-4"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
            >
              <h2 className="text-xs font-semibold text-white mb-4">Formulaire de Demande</h2>

              <form onSubmit={handleSubmit} className="space-y-2.5" data-testid="conciergerie-form">
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label className="text-[#94A3B8] text-xs">Nombre d'Adultes *</Label>
                    <Input type="number" min="1" value={formData.num_adults}
                      onChange={(e) => setFormData({...formData, num_adults: parseInt(e.target.value) || 1})}
                      className="bg-white/5 border-white/10 text-white text-xs h-8" data-testid="input-adults" />
                  </div>
                  <div>
                    <Label className="text-[#94A3B8] text-xs">Nombre de Mineurs</Label>
                    <Input type="number" min="0" value={formData.num_minors}
                      onChange={(e) => setFormData({...formData, num_minors: parseInt(e.target.value) || 0})}
                      className="bg-white/5 border-white/10 text-white text-xs h-8" data-testid="input-minors" />
                  </div>
                </div>

                <div>
                  <Label className="text-[#94A3B8] text-xs">Nom Complet *</Label>
                  <Input value={formData.full_name} onChange={(e) => setFormData({...formData, full_name: e.target.value})}
                    required placeholder="Nom complet" className="bg-white/5 border-white/10 text-white text-xs h-8" data-testid="input-name" />
                </div>

                <div>
                  <Label className="text-[#94A3B8] text-xs">Entreprise (optionnel)</Label>
                  <Input value={formData.company_name} onChange={(e) => setFormData({...formData, company_name: e.target.value})}
                    placeholder="Nom de l'entreprise" className="bg-white/5 border-white/10 text-white text-xs h-8" data-testid="input-company" />
                </div>

                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label className="text-[#94A3B8] text-xs">Email *</Label>
                    <Input type="email" value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})}
                      required placeholder="votre@email.com" className="bg-white/5 border-white/10 text-white text-xs h-8" data-testid="input-email" />
                  </div>
                  <div>
                    <Label className="text-[#94A3B8] text-xs">Téléphone *</Label>
                    <Input value={formData.phone} onChange={(e) => setFormData({...formData, phone: e.target.value})}
                      required placeholder="+243..." className="bg-white/5 border-white/10 text-white text-xs h-8" data-testid="input-phone" />
                  </div>
                </div>

                <div>
                  <Label className="text-[#94A3B8] text-xs">Service Demandé *</Label>
                  <Select value={formData.service} onValueChange={(value) => setFormData({...formData, service: value})}>
                    <SelectTrigger className="bg-white/5 border-white/10 text-white text-xs h-8" data-testid="select-service">
                      <SelectValue placeholder="Choisir un service" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#1E293B] border-white/10 max-h-[300px]">
                      {services.map((service, index) => (
                        <SelectItem key={index} value={service} className="text-xs">{service}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label className="text-[#94A3B8] text-xs">Niveau d'Urgence</Label>
                  <RadioGroup value={formData.urgency} onValueChange={(value) => setFormData({...formData, urgency: value})}
                    className="grid grid-cols-2 gap-2 mt-1">
                    {urgencyLevels.map((level) => (
                      <div key={level.value} className="flex items-center space-x-2">
                        <RadioGroupItem value={level.value} id={level.value} className="border-white/30" data-testid={`urgency-${level.value}`} />
                        <Label htmlFor={level.value} className={`font-normal text-xs ${level.color}`}>{level.label}</Label>
                      </div>
                    ))}
                  </RadioGroup>
                </div>

                <div>
                  <Label className="text-[#94A3B8] text-xs">Informations Supplémentaires</Label>
                  <Textarea value={formData.additional_info} onChange={(e) => setFormData({...formData, additional_info: e.target.value})}
                    placeholder="Toute information utile..." rows={2} className="bg-white/5 border-white/10 text-white text-xs resize-none" data-testid="input-additional" />
                </div>

                {!formData.service && (
                  <div className="flex items-center gap-2 text-amber-400 bg-amber-500/10 p-2 rounded-lg border border-amber-500/20">
                    <AlertCircle className="w-4 h-4" />
                    <span className="text-xs">Veuillez sélectionner un service</span>
                  </div>
                )}

                <button type="submit" className="w-full btn-gold py-2.5 text-xs" disabled={submitting || !formData.service} data-testid="submit-btn">
                  {submitting ? 'Envoi...' : 'Soumettre la Demande'}
                </button>
              </form>
            </motion.div>
          </div>
        </div>
      </section>

      <SuccessModal 
        isOpen={showSuccess} 
        onClose={() => setShowSuccess(false)}
        title="Demande envoyée !"
        message="Votre demande de conciergerie a été transmise avec succès. Notre équipe vous contactera sous peu."
      />
    </div>
  );
};

export default ConciergeriePage;
